import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import LoadingScreen from '@/components/LoadingScreen';
import { resolvePostLoginDestination } from '@/lib/authRedirect';
import { clearAuthTransitionPending } from '@/lib/authTransition';
import { clearAuthFlowTrace, traceAuth } from '@/lib/authTrace';

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const sanitizeTargetPath = (value: string | null): string => {
  if (!value || typeof window === 'undefined') return '/';
  try {
    const decoded = decodeURIComponent(value);
    if (!decoded.startsWith('/')) return '/';
    return decoded;
  } catch {
    return '/';
  }
};

const AuthProcessing = () => {
  const { user, loading, refreshSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    traceAuth('auth.processing.mounted', { search: location.search });

    const run = async () => {
      if (loading) {
        traceAuth('auth.processing.waiting_auth_loading');
        return;
      }

      const search = new URLSearchParams(location.search);
      const target = sanitizeTargetPath(search.get('target'));
      traceAuth('auth.processing.target_resolved', { target });

      if (user) {
        const destination = resolvePostLoginDestination(target);
        traceAuth('router.final_destination', {
          from: '/auth/processing',
          to: destination,
          reason: 'user_present'
        });
        clearAuthTransitionPending();
        clearAuthFlowTrace();
        navigate(destination, { replace: true });
        return;
      }

      // OAuth callbacks can race with cookie persistence; retry briefly before failing.
      const retryDelays = [250, 500, 900];
      for (const delay of retryDelays) {
        if (cancelled) return;
        await wait(delay);
        traceAuth('auth.processing.retry_refresh_session', { delayMs: delay });
        const refreshedUser = await refreshSession();
        if (cancelled) return;
        if (refreshedUser) {
          const destination = resolvePostLoginDestination(target);
          traceAuth('router.final_destination', {
            from: '/auth/processing',
            to: destination,
            reason: 'refresh_session_success'
          });
          clearAuthTransitionPending();
          clearAuthFlowTrace();
          navigate(destination, { replace: true });
          return;
        }
      }

      traceAuth('router.final_destination', {
        from: '/auth/processing',
        to: '/',
        reason: 'refresh_session_timeout'
      });
      clearAuthTransitionPending();
      clearAuthFlowTrace();
      navigate('/', { replace: true });
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [loading, location.search, navigate, refreshSession, user]);

  return (
    <LoadingScreen />
  );
};

export default AuthProcessing;
